import React, { useContext } from "react";
import compose from "compose-function";
import moment from "moment";

import TableComponent from "../../components/TableComponent";
import CreateItemButton from "../../components/CreateItemButton";

import { useColumns } from "../../hooks/useColumns";
import useUserPermissionsContext from "../../hooks/useUserPermissionsContext";

import {
  getSortOrder,
  sortColumns,
  sortModelToString,
  fetchDataWithOptionsAndFilters,
  exportToFile,
} from "../../utils/utils";

import {
  addHrefToRow,
  changeDeletedToBoolean,
  addValueFormatter,
  padValueWith,
  readDataFromLocalStorage,
  getDefaultFilterModel,
  getInitialFilters,
  generateEntityKeyForLocalStorage,
  addFormatters,
} from "../../utils/table";

import { hardDelete, softDelete } from "../../dao/operations";
import { FILTER_TYPES } from "../../dao/types";
import { copyRecord, editRecords, exportRecords } from "../../dao/common";

import { getDefaultSort } from "../../constants/defaultSorting";

import UserRights from "../../enums/UserRights";

import { ROUTES } from "../../enums/Routes";
import { API_ROUTES } from "../../enums/api";
import { addSearchParams } from "../../utils/dao";

import { GlobalContext } from "../../globalContext";
import useEntities from "../../hooks/useEntities";
import { useSortModel } from "../../hooks/useSortModel";
import { EQUIPMENT_RELATIONAL_FIELDS } from "../../constants/softDelete";

const API_ROUTE = API_ROUTES.equipments;
/**
 * Here we need to transform the row data retrieved,
 * in order to make it compatible with @mui/x-data-grid-pro
 */
const transformRows = (rows) => {
  return rows.reduce((acc, row) => {
    return [
      ...acc,
      compose(
        changeDeletedToBoolean("deleted"),
        addHrefToRow({
          field: "id",
          slug: "equipment",
        })
      )(row),
    ];
  }, []);
};

/**
 * Here we need to transform the column data retrieved,
 * in order to add @mui/x-data-grid-pro formatters
 */
const transformColumns = (columns) => {
 
  // TODO can we abstract this using fields metadata?
  // maybe using max_length + decimals + pad property?

  return addFormatters(
    [
      { field: "system_group_number", formatter: addValueFormatter("system_group_number")(padValueWith(3)) },
      { field: "number", formatter: addValueFormatter("number")(padValueWith(4)) },
      { field: "system_number", formatter: addValueFormatter("system_number")(padValueWith(3)) },
      { field: "object_number", formatter: addValueFormatter("object_number")(padValueWith(5)) },
    ],
    columns);
};

export default function EquipmentsContainer({
  nestedEntity = false,
  parent = {},
}) {
  const { globalFilters } = useContext(GlobalContext);

  // Get cached local filter model from local storage
  // with a fallback to default field values
  const cachedLocalFilter = readDataFromLocalStorage(
    generateEntityKeyForLocalStorage(FILTER_TYPES.equipment, "filterModel", {
      nestedEntity,
    })
  ) ?? {
    filterModel: null,
    filterJSON: "",
  };

  // destructure fields from cachedLocalFilter
  const {
    filterModel: cachedLocalFilterModel,
    filterJSON: cachedFilterJSONString,
  } = cachedLocalFilter;

  const initialFilters = getInitialFilters({
    cache: {
      localFilterModel: cachedLocalFilterModel,
      filterJSONString: cachedFilterJSONString,
    },
    defaultFilterModel: getDefaultFilterModel("object_number").filterModel,
  });

  const { columns } = useColumns(FILTER_TYPES.equipment);

  const defaultSortString = getDefaultSort(FILTER_TYPES.equipment);

  const { sortModel, setSortModel } = useSortModel(FILTER_TYPES.equipment, {
    nestedEntity,
    columns,
  });

  const sortModelString =
    sortModelToString(sortModel, columns) || defaultSortString;

  const {
    data,
    loadingState,
    fetchEntitiesData: fetchEquipmentsData,
    setFilterString,
    setOffset,
    limit,
    offset,
    setLimit,
    totalNumberOfRecords,
  } = useEntities("equipments", {
    params: {
      object_ids: parent?.object_id ?? globalFilters?.value ?? null,
      location_ids: parent.location_id ?? null,
      system_ids: parent.system_id ?? null,
      section_ids: parent.section_id ?? null,
    },
    initialFilters: initialFilters.apiFilterString,
    order: sortModelString,
  });

  const fetchRecordsDataWithGlobalFilters = fetchDataWithOptionsAndFilters(
    fetchEquipmentsData
  )({
    limit,
    offset,
    order: sortModelString,
    params: {
      object_ids: parent?.object_id ?? globalFilters?.value ?? null,
      location_ids: parent.location_id ?? null,
      system_ids: parent.system_id ?? null,
      section_ids: parent.section_id ?? null,
    },
  });

  const fetchExportData = async (filterString, selectedColumnsString) => {
    const abortController = new AbortController();
    const searchParams = {
      filters: filterString,
      columns: selectedColumnsString,
      object_ids: parent?.object_id ?? globalFilters?.value ?? null,
      location_ids: parent.location_id ?? null,
      system_ids: parent.system_id ?? null,
      section_ids: parent.section_id ?? null,
    };
    const response = await exportRecords(
      API_ROUTE,
      searchParams,
      abortController
    );

    exportToFile(
      `export_${FILTER_TYPES.equipment}_overview_${moment().format(
        "DD-MM-YYYY"
      )}.csv`,
      response
    );

    return "Export done";
  };

  const { isAllowedTo } = useUserPermissionsContext();

  const isAllowedToCreate = isAllowedTo(
    UserRights.CREATE,
    FILTER_TYPES.equipment
  );
  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.equipment
  );
  const isAllowedToExport = isAllowedTo(UserRights.EXPORT_CSV);
  const isAllowedToMassEdit = isAllowedTo(UserRights.MASS_EDIT_EQUIPMENT);
  const isAllowedToInlineEdit = isAllowedTo(UserRights.INLINE_EDIT_EQUIPMENT);

  const tableOptions = {};
  tableOptions.hasDelete = isAllowedToDelete;
  tableOptions.hasCopy = isAllowedToCreate;
  tableOptions.hasMassEdit = isAllowedToMassEdit;
  tableOptions.sortOrder = getSortOrder(FILTER_TYPES.equipment);
  tableOptions.hasInlineEdit = isAllowedToInlineEdit;

  const searchParams = new URLSearchParams(
    Object.entries({
      objectId: parent?.object_id || globalFilters?.value,
      systemGroupId: parent.system_group_id, // defined when sub overview
      systemId: parent.system_id, // defined when sub overview
      sectionId: parent.section_id, // defined when sub overview
      sectionReference: parent.section_reference, // defined when sub overview
      locationId: parent.location_id, // defined when sub overview
    }).filter(([, value]) => value)
  ).toString();

  return (
    <>
      <TableComponent
        {...{
          columns: transformColumns(sortColumns(columns)),
          transformColumns,
          loading: loadingState,
          rows: transformRows(data),
          refreshData: fetchRecordsDataWithGlobalFilters,
          renderCreateEntityButton: () => (
            <CreateItemButton
              to={addSearchParams(ROUTES.equipment.create)(searchParams)}
              isDisabled={!isAllowedToCreate}
              entity={FILTER_TYPES.equipment}
            />
          ),
          copyEntry: copyRecord,
          editRecords,
          softDeleteEntry: softDelete({
            route: API_ROUTE,
            delete: true,
            includeFields: EQUIPMENT_RELATIONAL_FIELDS,
          }),
          hardDeleteEntry: hardDelete({
            route: API_ROUTE,
          }),
          restoreEntry: softDelete({
            route: API_ROUTE,
            delete: false,
            includeFields: EQUIPMENT_RELATIONAL_FIELDS,
          }),
          tableOptions,
          setFilters: setFilterString,
          setOffset,
          offset,
          setLimit,
          sortModel,
          setSortModel,
          numberOfRecords: totalNumberOfRecords,
          isAllowedToExport,
          entity: FILTER_TYPES.equipment,
          nestedEntity: nestedEntity,
          initialFilterModel: initialFilters.grid,
          exportRecords: fetchExportData,
        }}
      />
    </>
  );
}
